import React, { useEffect, useState, useContext } from 'react';

import Paper from '@material-ui/core/Paper';
import { navigate } from '@reach/router';

import { GlobalContext } from '../../../../store';
import { IEventRound } from '../../../../store/admin/eventRounds/store';
import { IMessageNotification } from '../../../../store/notification/store';
import { ISource, IStripeToken } from '../../../../store/user/store';

import Button from '../../../../components/Button';
import CouponPicker from '../../../../components/CouponPicker';
import Modal from '../../../../components/Modal';
import PaymentManager from '../../../../components/PaymentManager';
import { EventWizardStepTitle } from '../Components/EventWizardStepTitle/EventWizardStepTitle';
import { TotalPlayersAndCharges } from './TotalPlayersAndCharges';
import { useAdminRound } from '../../../../contrib/Hooks/useAdminRound.hook';
import { useGetAdminEvent } from '../../../../contrib/Hooks/useGetAdminEvent';
import { GamesAreNotCompleteAlert } from './GamesAreNotCompleteAlert.Styles';

interface IProps {
  clubId: string;
  eventId: string;
  roundId: string;
}

const LaunchRound = (props: IProps) => {
  const { clubId, eventId, roundId } = props;

  const {
    actions,
    actions: {
      adminRoundPayments,
      notifications,
      roundTees: { getRoundTees }
    },
    adminClubCreditCard: adminClubCreditCard2,
    adminRoundPayments: roundPayment,
    user,
    roundTees: { assignation: teeAssignation }
  } = useContext(GlobalContext);

  const event = useGetAdminEvent(eventId);
  const [round] = useAdminRound(roundId);

  const [selectedCardIdValue, setSelectedCardId] = useState(0);
  const [successModalIsOpenValue, setSuccessModalIsOpen] = useState(false);
  const [newCardValue, setNewCard] = useState<IStripeToken>();
  const [adminClubCreditCard, setClubCreditCard] = useState<ISource[]>([]);
  const [launchDisabled, setLaunchDisabled] = useState(false);
  const [gamesAreComplete, setGamesAreComplete] = useState(true);

  const payment = roundPayment[Number(roundId)];

  const roundLaunched =
    !!round && round.status === 'published' && payment && payment.subtotalPrice === 0;

  const sources = newCardValue
    ? [
        ...adminClubCreditCard,
        {
          ...newCardValue.card,
          djstripeId: Number(newCardValue.card.last4)
        }
      ]
    : adminClubCreditCard;

  useEffect(() => {
    setClubCreditCard(adminClubCreditCard2[Number(clubId)]);
  }, [adminClubCreditCard2, clubId]);

  useEffect(() => {
    if (clubId && eventId && roundId) {
      adminRoundPayments.get(Number(clubId), Number(eventId), Number(roundId));
    }
  }, [roundId]);

  const onSave = async (newRound: Partial<IEventRound>) => {
    if (event) {
      const response = await actions.adminEvents.editRound(Number(clubId), event, newRound as any);

      return response;
    }
  };

  const launchRound = async () => {
    const coupon = payment && payment.coupon ? payment.coupon.codename : '';
    let response: any;

    if (event && event.processPayment && round) {
      const publishRound: Partial<IEventRound> = { status: 'published', id: round.id };

      response = await onSave(publishRound);
    } else {
      const paymentMethod =
        (adminClubCreditCard || []).find(p => p.djstripeId === selectedCardIdValue) || newCardValue;

      response = await adminRoundPayments.launch(
        Number(clubId),
        Number(eventId),
        Number(roundId),
        paymentMethod as ISource | IStripeToken,
        coupon
      );
    }

    if (response.errors) {
      const newNotification: IMessageNotification = {
        status: 'read',
        text: response.errors.adminRoundPayments.msg,
        type: 'error'
      };

      notifications.push(newNotification);
    } else {
      adminRoundPayments.get(Number(clubId), Number(eventId), Number(roundId));

      setSuccessModalIsOpen(true);
    }
  };

  const setCoupon = async (code: string) => {
    const response = await adminRoundPayments.get(
      Number(clubId),
      Number(eventId),
      Number(roundId),
      code
    );

    return response.errors ? response : (response as any).adminRoundPayments[roundId];
  };

  const onContinue = () =>
    navigate(`/staff/club/${clubId}/event/${eventId}/round/${roundId}/results`);

  const onSelectPaymentMethod = async (
    _: number,
    paymentMethod: IStripeToken | ISource
  ): Promise<any> => {
    if ((paymentMethod as ISource).djstripeId) {
      setSelectedCardId((paymentMethod as ISource).djstripeId);
    } else {
      // new cards does not have a djstripeid, so we will fake it to make them selectable
      setNewCard(paymentMethod as IStripeToken);

      setSelectedCardId(Number((paymentMethod as IStripeToken).card.last4));
    }
  };

  const modalToggleSuccess = (): void => setSuccessModalIsOpen(!successModalIsOpenValue);

  const goToDashboard = async () => {
    setSuccessModalIsOpen(false);

    navigate(`/staff/club/${clubId}`);
  };

  const goToReviewScores = async () => {
    setSuccessModalIsOpen(false);

    onContinue();
  };

  useEffect(() => {
    if (round && round.groups) {
      const games = round.groups.flatMap(group => group.games);

      const roundGamesAreComplete = games.reduce((areComplete, game) => {
        return areComplete && !!game.tee;
      }, true);

      setGamesAreComplete(roundGamesAreComplete);
    }
  }, [round]);

  useEffect(() => {
    const launchShouldBeDisabled =
      (event &&
        !event.processPayment &&
        !selectedCardIdValue &&
        payment &&
        payment.totalPrice > 0) ||
      !payment ||
      !gamesAreComplete ||
      teeAssignation.length === 0;

    setLaunchDisabled(launchShouldBeDisabled);
  }, [event, selectedCardIdValue, gamesAreComplete, payment]);

  useEffect(() => {
    if (clubId && eventId && roundId) {
      getRoundTees(Number(clubId), Number(eventId), Number(roundId)).catch();
    }
  }, [clubId, eventId, roundId]);

  return (
    <div className={`event-wizard-page launch-round small-content-page`}>
      <div className="launch-round-container">
        <EventWizardStepTitle
          buttonText="Launch round"
          buttonOnClick={launchRound}
          buttonDisabled={launchDisabled}
          title="LAUNCH ROUND"
        />

        {!payment ? (
          <div className="lazy-loader" />
        ) : (
          <>
            {!gamesAreComplete && (
              <GamesAreNotCompleteAlert>
                <p>
                  The round could not be launched because some players need to be assigned either
                  flight, tee or index. Please, fix this before trying to launch the round
                </p>
              </GamesAreNotCompleteAlert>
            )}

            {roundLaunched ? (
              <p style={{ textAlign: 'center' }}>This round is already launched.</p>
            ) : (
              <>
                <p className="desc-text">
                  Once the first round is launched all of the settings for that round are locked,
                  player registration is closed, and players will be notified of thier tee times,
                  playing partners, tees and handicaps.
                </p>

                <p className="desc-text">
                  Only admins may edit the tee sheet for this round including adding new players to
                  the event.
                </p>

                {payment.items.freeTrials > 0 && (
                  <p className="desc-text" style={{ color: 'var(--mainColor)' }}>
                    THIS EVENT IS PART OF YOUR FREE TRIAL AND WILL NOT BE CHARGED. <br />
                    YOU STILL HAVE {payment.items.freeTrials - 1} REMAINING TRIAL EVENTS
                  </p>
                )}
              </>
            )}

            {event && !event.processPayment && !!payment && payment.subtotalPrice > 0 && (
              <Paper elevation={1} className={`paper`}>
                <h3>THE CLUB’S CARD ON FILE WILL BE CHARGED</h3>

                <p className="desc-text">
                  When the round is launched the club’s card on file will be charged $3 for every
                  registerd player.
                </p>

                <TotalPlayersAndCharges payment={payment} />

                <CouponPicker target="event" id={roundId} onChange={setCoupon} />

                {payment && payment.totalPrice > 0 && (
                  <div className="payment-method-container">
                    <PaymentManager
                      actions={actions}
                      clubId={Number(clubId)}
                      next={onSelectPaymentMethod}
                      nextButton="Save Card"
                      selectedCardId={selectedCardIdValue}
                      sources={sources}
                      textButton="ADD A NEW CREDIT CARD"
                      user={user}
                    />
                  </div>
                )}
              </Paper>
            )}
          </>
        )}

        <Modal
          onClose={modalToggleSuccess}
          open={successModalIsOpenValue}
          className="round-launch-confirm-modal staff-scope"
          id="round-launched-modal"
          title="LAUNCH ROUND"
          wideButton={true}
          actions={[
            <Button
              key="cancel-round-launch"
              onClick={goToDashboard}
              variant="contained"
              color="primary"
              fullWidth={true}
            >
              Back to dashboard
            </Button>,
            <Button
              key="confirm-round-launch"
              fullWidth={true}
              onClick={goToReviewScores}
              variant="outlined"
              className="confirm-removal-button"
              color="primary"
            >
              Review scores
            </Button>
          ]}
        >
          <p>The round has been successfully launched. Where would you like to go?</p>
        </Modal>
      </div>
    </div>
  );
};

export default LaunchRound;
